import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { AuthLayout, Field, inputCls, primaryBtn } from "@/components/AuthLayout";
import { PasswordInput } from "@/components/PasswordInput";
import { api, errMsg } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [f, setF] = useState({ next: "", confirm: "" });
  const [done, setDone] = useState(false);
  const [error, setError] = useState(token ? "" : "This reset link is incomplete. Please request a new one.");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (f.next.length < 8) return setError("New password must be at least 8 characters.");
    if (f.next !== f.confirm) return setError("Passwords do not match.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: f.next });
      setDone(true);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Choose a new password" subtitle="Use at least 8 characters." side={{ title: "Secure your Comfort Taxi account.", text: "Once saved, sign in with your new password." }}>
      {done ? (
        <div data-testid="reset-success-message" className="rounded-2xl border border-emerald-100 bg-emerald-50 p-5 text-sm text-emerald-900">
          <CheckCircle2 className="mb-2 h-6 w-6 text-emerald-600" />
          Your password has been reset.
          <Link to="/login" data-testid="reset-to-login-link" className={`${primaryBtn} mt-4`}>Sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
          <Field label="New password">
            <PasswordInput testId="reset-password-input" required className={inputCls} value={f.next} onChange={(e) => setF({ ...f, next: e.target.value })} />
          </Field>
          <Field label="Confirm new password">
            <PasswordInput testId="reset-confirm-input" required className={inputCls} value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} />
          </Field>
          {error && <p data-testid="reset-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
          <button type="submit" data-testid="reset-submit-button" disabled={busy || !token} className={primaryBtn}>{busy ? "Saving…" : "Save new password"}</button>
          <p className="text-sm text-slate-500"><Link to="/forgot-password" data-testid="reset-request-new-link" className="font-semibold text-blue-700">Request a new link</Link></p>
        </form>
      )}
    </AuthLayout>
  );
}
